import { create } from 'zustand';
import type { ReactNode } from 'react';

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface ToastItem {
  id: string;
  title: string;
  body?: ReactNode;
  type?: 'info' | 'success' | 'warning' | 'error';
  duration?: number;
  /** 되돌릴 자리 — 읽기 실패 토스트의 "다시 시도". 누르면 onClick 뒤에 토스트가 닫힌다. */
  action?: ToastAction;
}

interface ToastState {
  toasts: ToastItem[];
  exitingIds: Set<string>;
  addToast: (toast: Omit<ToastItem, 'id'>) => void;
  removeToast: (id: string) => void;
  startExiting: (id: string) => void;
}

const timerMap = new Map<string, ReturnType<typeof setTimeout>>();

export const useToastStore = create<ToastState>((set, get) => ({
  toasts: [],
  exitingIds: new Set(),
  addToast: (toast) => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    set((state) => ({
      toasts: [...state.toasts, { ...toast, id }],
    }));
    // Auto-dismiss with exit animation
    const duration = toast.duration ?? 5000;
    timerMap.set(
      id,
      setTimeout(() => {
        timerMap.delete(id);
        get().startExiting(id);
      }, duration)
    );
  },
  startExiting: (id) => {
    set((state) => ({
      exitingIds: new Set([...state.exitingIds, id]),
    }));
  },
  removeToast: (id) => {
    const timer = timerMap.get(id);
    if (timer) {
      clearTimeout(timer);
      timerMap.delete(id);
    }
    set((state) => ({
      toasts: state.toasts.filter((t) => t.id !== id),
      exitingIds: new Set([...state.exitingIds].filter((eid) => eid !== id)),
    }));
  },
}));
